import type {
  GiftCardPublicConfigResponseObject,
  GiftCardCheckoutRequestObject,
  GiftCardCheckoutResponseObject,
  GiftCardCartSummaryRequestObject,
  GiftCardCartSummaryResponseObject,
} from '@cfixe-monorepo/shared';
import {
  ProPaymentStatus,
  minorUnitToSiteEventValue,
  public_env,
  resolveSiteEventCurrency,
  toE164IfInternational,
} from '@cfixe-monorepo/shared';
import {
  fetchGiftCardConfig,
  createGiftCardCheckout,
  fetchGiftCardCartSummary,
} from '../../website/api/professional-api';
import { getRequiredCaptchaToken, type CapRef } from '../../website/functions/captcha';
import {
  takeSiteEventReserve,
  toSiteEventItems,
  writeSiteEventReserve,
  type ReservedSiteEvent,
} from '../../website/functions/site-event-reserve';
import type { ProPaymentDetails, ProPaymentStatusResponse } from '../../pro-payment';

/** Événement `purchase` prêt à émettre (achat d'une carte cadeau ou d'un forfait). */
export type PurchaseTrackingEvent = ReservedSiteEvent<'purchase'>;

export interface CreateGiftCardCallbacksOptions {
  /**
   * Événement de suivi `purchase`, émis une fois quand le paiement en ligne est confirmé. Rangé
   * en `sessionStorage` au checkout (cf. site-event-reserve.ts) : n'est PAS appelé quand la
   * réserve est absente (confirmation rechargée, paiement terminé dans un autre navigateur).
   */
  onPurchaseTrackingEvent?: (event: PurchaseTrackingEvent) => void;
}

/** Callbacks injectés dans le PurchaseTunnel (mirroir de createBookingCallbacks). */
export interface GiftCardCallbacks {
  onLoadConfig: () => Promise<GiftCardPublicConfigResponseObject>;
  /** Aperçu panier (mini-panier + total formaté serveur). Appelé à chaque changement de sélection. */
  onPreview: (payload: GiftCardCartSummaryRequestObject) => Promise<GiftCardCartSummaryResponseObject>;
  onCheckout: (payload: GiftCardCheckoutRequestObject) => Promise<GiftCardCheckoutResponseObject>;
  /** Détails du pro_payment (client_secret Stripe + compte Connect) — paiement inline style book. */
  onLoadPaymentDetails: (uuid: string, capToken?: string) => Promise<ProPaymentDetails>;
  /** Statut du pro_payment (poll après confirmation Stripe). */
  onPollPaymentStatus: (uuid: string) => Promise<ProPaymentStatusResponse>;
}

/**
 * Fabrique les callbacks du tunnel d'achat de carte cadeau pour un pro donné.
 * Le captcha (Cap.js) est résolu côté checkout, comme pour la réservation.
 */
export function createGiftCardCallbacks(
  professionalSlug: string,
  options?: CreateGiftCardCallbacksOptions,
): GiftCardCallbacks {
  const captchaRef: CapRef = { current: null };
  /* La réserve de suivi n'est tenue que si un consommateur l'écoute. */
  const tracksPurchase = options?.onPurchaseTrackingEvent !== undefined;

  return {
    onLoadConfig: (): Promise<GiftCardPublicConfigResponseObject> => fetchGiftCardConfig(professionalSlug),
    onPreview: (payload): Promise<GiftCardCartSummaryResponseObject> =>
      fetchGiftCardCartSummary(professionalSlug, payload),
    onCheckout: async (payload): Promise<GiftCardCheckoutResponseObject> => {
      const token = await getRequiredCaptchaToken(captchaRef);
      const result = await createGiftCardCheckout(professionalSlug, payload, token);
      if (tracksPurchase && result.payment_uuid) {
        writeSiteEventReserve<'purchase'>(result.payment_uuid, {
          params: {
            transaction_id: result.payment_uuid,
            value: minorUnitToSiteEventValue(result.amount),
            currency: resolveSiteEventCurrency(result.currency),
            items: toSiteEventItems(result.tracking_items),
          },
          userData: {
            email: payload.buyer.email,
            phone_number: toE164IfInternational(payload.buyer.phone_number),
          },
        });
      }
      return result;
    },
    onLoadPaymentDetails: async (uuid: string, capToken?: string): Promise<ProPaymentDetails> => {
      const res = await fetch(`${public_env.API_ENDPOINT}/public/pro-payment/${uuid}`, {
        headers: capToken ? { 'x-cap-token': capToken } : undefined,
      });
      if (!res.ok) throw new Error('pro-payment details failed');
      return (await res.json()) as ProPaymentDetails;
    },
    onPollPaymentStatus: async (uuid: string): Promise<ProPaymentStatusResponse> => {
      const res = await fetch(`${public_env.API_ENDPOINT}/public/pro-payment/${uuid}/status`);
      if (!res.ok) throw new Error('pro-payment status failed');
      const data = (await res.json()) as ProPaymentStatusResponse;
      /* Le poll est répétitif : la réserve n'est reprise qu'à la première réponse « réglée ». */
      if (tracksPurchase && data.status === ProPaymentStatus.PAYED) {
        const event = takeSiteEventReserve<'purchase'>(uuid);
        if (event) options?.onPurchaseTrackingEvent?.(event);
      }
      return data;
    },
  };
}
